<template>
  <v-container
    class="tag-list-height ot-management-listing"
    :class="internal ? 'pa-0' : ''"
    fluid
    v-if="getPermission('setting:view')"
  >
    <v-layout class="pb-4">
      <v-flex>
        <h3
          class="font-weight-700 mb-0 font-size-24 custom-headline color-custom-blue"
        >
          OT Management
        </h3>
      </v-flex>

      <div>
        <v-btn
          depressed
          small
          class="text-white mr-2"
          color="cyan"
          @click="openOtDialog"
          :loading="dataLoading"
        >
          <span class="font-size-16 font-weight-600">Create</span>
        </v-btn>
      </div>
      <div class="" v-if="!internal">
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          v-on:click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </div>
    </v-layout>
    <ListingTable :columnCount="7" :rowData="templateDataArr">
      <template v-slot:thead>
        <thead>
          <tr>
            <th class="simple-table-th" width="20%">Shift Type</th>
            <th class="simple-table-th" width="20%">OT Type</th>
            <th class="simple-table-th">Day</th>
            <th class="simple-table-th">Start Time</th>
            <th class="simple-table-th">End Time</th>
            <th class="simple-table-th">Added By</th>
            <th class="simple-table-th text-end" width="10%">Action</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="paginatedOtList.length">
            <tr
              v-for="(row, index) in paginatedOtList"
              :key="index"
              class="alternate-listing-row"
            >
              <td class="simple-table-td text-capitalize pl-4">
                {{ getShiftType(row.shift_type) }}
              </td>
              <td class="simple-table-td text-capitalize pl-4">
                {{ getOtType(row.ot_type) }}
              </td>
              <td class="simple-table-td text-capitalize pl-4">
                {{ getDay(row.day) }}
              </td>
              <td class="simple-table-td text-capitalize pl-4">
                {{ formatOtTime(row.start_time) }}
              </td>
              <td class="simple-table-td text-capitalize pl-4">
                {{ formatOtTime(row.end_time) }}
              </td>
              <td class="simple-table-td pl-4">
                <TableActivity v-if="!lodash.isEmpty(row.added_by)" :data="row">
                  <template v-slot:first_letters>
                    {{ getFirstLetters(row.display_name) }}
                  </template>
                  <template v-slot:display_name>
                    {{ row.display_name }}
                  </template>
                  <template v-slot:date_time>
                    <template>{{ row.added_at }}</template>
                  </template>
                  <template v-slot:format_date_time>
                    {{ row.added_at }}
                  </template>
                </TableActivity>
              </td>
              <td class="simple-table-td text-end">
                <v-icon
                  color="cyan"
                  class="me-2"
                  size="20"
                  @click="editOt(row)"
                >
                  mdi-pencil</v-icon
                >
                <v-icon color="red" size="20" v-on:click="deleteJob(row.id)">
                  mdi-delete</v-icon
                >
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="5">
                <p
                  class="m-0 row-not-found text-center font-weight-500 font-size-16"
                >
                  <img
                    :src="$assetURL('media/error/empty.png')"
                    class="row-not-found-image"
                  />
                  Uhh... There are no data at the moment.
                </p>
              </td>
            </tr>
          </template>
        </tbody>
      </template>
    </ListingTable>
    <v-row
      v-if="paginatedOtList.length > 0"
      justify="end"
      class="align-center pagination_btn listing-footer ot-management-listing-footer px-2"
    >
      <p class="m-0 listing-footer-count">
        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
        {{ Math.min(currentPage * itemsPerPage, templateDataArr.length) }}
        of {{ templateDataArr.length }} entries
      </p>
      <v-spacer></v-spacer>
      <v-pagination
        v-model="currentPage"
        :length="pageCount"
        total-visible="7"
        color="cyan"
        @input="handlePageChange"
      ></v-pagination>
    </v-row>

    <Dialog :commonDialog.sync="OTDialog" :dialog-width="600">
      <template v-slot:title>
        <template v-if="otPayload.id">Edit</template>
        <template v-else>Add</template> OT
      </template>

      <template v-slot:body>
        <v-container fluid>
          <v-form ref="otForm" v-model="formValid" lazy-validation>
            <v-row>
              <v-col md="12">
                <label class="fw-500 mb-1 required">Shift Type</label>
                <v-select
                  :items="shifts"
                  v-model.trim="otPayload.shift_type"
                  @change="handleShiftTypeChange"
                  dense
                  filled
                  placeholder="Shift Type"
                  solo
                  flat
                  item-color="cyan"
                  item-text="text"
                  item-value="value"
                  :rules="[
                    validateRules.required(otPayload.shift_type, 'Shift Type'),
                  ]"
                >
                </v-select>
              </v-col>
              <v-col md="12">
                <label class="fw-500 mb-1 required">OT Type</label>
                <v-select
                  :items="ot_types"
                  v-model.trim="otPayload.ot_type"
                  dense
                  filled
                  placeholder="OT Type"
                  solo
                  flat
                  item-color="cyan"
                  item-text="text"
                  item-value="value"
                  :rules="[
                    validateRules.required(otPayload.ot_type, 'OT Type'),
                  ]"
                >
                </v-select>
              </v-col>
              <v-col md="12">
                <label class="fw-500 mb-1 required">Day</label>
                <v-select
                  :items="weekDays"
                  v-model="otPayload.day"
                  @change="handleDayChange"
                  dense
                  filled
                  placeholder="Day"
                  solo
                  flat
                  item-color="cyan"
                  item-text="text"
                  item-value="value"
                  :disabled="!otPayload.shift_type"
                  :rules="[validateRules.required(otPayload.day, 'Day')]"
                >
                </v-select>
              </v-col>
              <v-col md="12">
                <label class="fw-500 mb-1 required">Start Time</label>
                <!-- <v-text-field
                v-model.trim="otPayload.start_time"
                placeholder="Enter Start Time"
                color="cyan"
                dense
                filled                 
                solo                   
                flat
                /> -->
                <TimePicker
                  key="start-time"
                  id="start-time"
                  type="start-time"
                  placeholder="Start Time"
                  v-model="otPayload.start_time"
                  :disabledStartTime="disabled_start_time"
                  :disabledEndTime="disabled_end_time"
                  :disabled="!otPayload.shift_type"
                  :rules="
                    touched.start_time || showErrors
                      ? [
                          validateRules.required(
                            otPayload.start_time,
                            'Start Time'
                          ),
                        ]
                      : []
                  "
                />
              </v-col>
              <v-col md="12">
                <label class="fw-500 mb-1 required">End Time</label>
                <TimePicker
                  key="end-time"
                  id="end-time"
                  type="end-time"
                  placeholder="End Time"
                  v-model="otPayload.end_time"
                  :disabledStartTime="disabled_start_time"
                  :disabledEndTime="disabled_end_time"
                  :disabled="!otPayload.shift_type"
                  :rules="
                    touched.end_time || showErrors
                      ? [validateRules.required(otPayload.end_time, 'End Time')]
                      : []
                  "
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </template>

      <template v-slot:action>
        <v-btn
          @click="saveOt"
          :disabled="pageLoading || !formValid"
          color="cyan white--text"
          >Save</v-btn
        >
        <v-btn @click="OTDialog = false">Close</v-btn>
      </template>
    </Dialog>
    <DeleteDialog :delete-dialog="deleteCategoryDialog">
      <template v-slot:title>Delete Confirmation</template>
      <template v-slot:text>
        <h3 class="font-size-18 font-weight-600 delete-text">
          Are you sure you want to delete this ?
        </h3>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          :disabled="deleteJobLoading"
          class="mx-2 custom-grey-border custom-bold-button"
          v-on:click="deleteCategoryDialog = false"
          >No! Cancel</v-btn
        >
        <v-btn
          depressed
          :disabled="deleteJobLoading"
          :loading="deleteJobLoading"
          color="red lighten-1 white--text"
          v-on:click="deleteOt()"
          class="mx-2 custom-grey-border custom-bold-button"
          >Yes! Delete</v-btn
        >
      </template>
    </DeleteDialog>
  </v-container>
</template>

<script>
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import DeleteDialog from "@/view/pages/partials/Delete";
import Dialog from "@/view/pages/partials/Dialog.vue";
import TableActivity from "@/view/pages/partials/Table-Activity.vue";
import {
  GET,
  DELETE,
  PATCH,
  QUERY,
} from "@/core/services/store/request.module";
import TimePicker from "@/view/components/main-job/TimePickerOtManagment.vue";
import ValidationMixin from "@/core/plugins/validation-mixin";
import moment from "moment";
export default {
  mixins: [CommonMixin, ValidationMixin],
  name: "serviceFormTemplateSetting",
  data() {
    return {
      type: null,
      rowsOffset: 0,
      isCheckbox: false,
      templateDataArr: [],
      formValid: true,
      showErrors: false,
      touched: {
        start_time: false,
        end_time: false,
      },
      deleteJobLoading: false,
      related_id: null,
      is_exist: 0,
      related_type: null,
      type_filter: null,
      OTDialog: false,
      deleteCategoryDialog: false,
      deleteId: false,
      confirm_dialog: false,
      confirm_loading: false,
      dataLoading: false,
      pageLoading: false,
      currentPage: 1,
      itemsPerPage: 10,
      totalPages: 0,
      totalRows: 0,
      otPayload: {
        shift_type: null,
        ot_type: null,
        day: null,
        start_time: "",
        end_time: "",
      },
      shifts: [
        { value: 1, text: "Day Shift" },
        { value: 2, text: "Night Shift" },
      ],
      ot_types: [
        { value: 1, text: "OT-1.5" },
        { value: 2, text: "OT-2" },
      ],
      weekDays: [
        { value: 1, text: "Monday" },
        { value: 2, text: "Tuesday" },
        { value: 3, text: "Wednesday" },
        { value: 4, text: "Thursday" },
        { value: 5, text: "Friday" },
        { value: 6, text: "Saturday" },
        { value: 7, text: "Sunday" },
      ],
      companyBusinessHours: [],
      companyNightBusinessHours: [],
      disabled_start_time: null,
      disabled_end_time: null,
    };
  },
  props: {
    internal: {
      type: Boolean,
      default: false,
    },
    module: {
      type: String,
      default: null,
    },
  },
  methods: {
    editOt(row) {
      let dayValue = null;
      if (Array.isArray(row.day)) {
        dayValue = row.day.length > 0 ? Number(row.day[0]) : null;
      } else if (typeof row.day === "string") {
        try {
          let parsed = JSON.parse(row.day);
          if (Array.isArray(parsed)) {
            dayValue = parsed.length > 0 ? Number(parsed[0]) : null;
          } else {
            let split = row.day.split(",");
            dayValue = split.length > 0 ? Number(split[0]) : null;
          }
        } catch (e) {
          let split = row.day.split(",");
          dayValue = split.length > 0 ? Number(split[0]) : null;
        }
      } else if (row.day) {
        dayValue = Number(row.day);
      }

      this.otPayload = {
        id: row.id,
        shift_type: Number(row.shift_type),
        ot_type: Number(row.ot_type),
        day: dayValue,
        start_time: row.start_time
          ? moment(row.start_time, "HH:mm:ss").format("hh:mm A")
          : null,

        end_time: row.end_time
          ? moment(row.end_time, "HH:mm:ss").format("hh:mm A")
          : null,
      };

      this.$nextTick(() => {
        console.log("ot payload", this.otPayload);
        this.OTDialog = true;
      });
    },
    deleteOt() {
      const _this = this;
      _this.$store
        .dispatch(DELETE, {
          url: `setting/ot-management/${_this.deleteId}`,
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.deleteCategoryDialog = false;
          _this.getOtTemplates();
        });
    },
    handleShiftTypeChange() {
      this.otPayload.day = null;
      this.otPayload.start_time = "";
      this.otPayload.end_time = "";
    },
    handleDayChange() {
      this.otPayload.start_time = "";
      this.otPayload.end_time = "";
      this.disabled_start_time = null;
      this.disabled_end_time = null;

      if (!this.otPayload.day || !this.otPayload.shift_type) return;

      let selectedDayObj = this.weekDays.find(
        (d) => d.value === this.otPayload.day
      );
      if (!selectedDayObj) return;

      let dayTitle = selectedDayObj.text;
      let hoursList =
        this.otPayload.shift_type === 1
          ? this.companyBusinessHours
          : this.companyNightBusinessHours;
      let matchedHour = hoursList.find(
        (h) => h.title && h.title.toLowerCase() === dayTitle.toLowerCase()
      );

      if (matchedHour) {
        let timeFormats = ["HH:mm:ss", "HH:mm", "hh:mm A"];
        this.disabled_start_time = matchedHour.start_time
          ? moment(matchedHour.start_time, timeFormats).format("hh:mm A")
          : null;
        this.disabled_end_time = matchedHour.end_time
          ? moment(matchedHour.end_time, timeFormats).format("hh:mm A")
          : null;
      }
    },
    openOtDialog() {
      this.otPayload = {
        shift_type: null,
        ot_type: null,
        day: null,
        start_time: "",
        end_time: "",
      };
      this.OTDialog = true;
    },
    getShiftType(val) {
      if (val == 1) {
        return "Day Shift";
      } else if (val == 2) {
        return "Night Shift";
      }
      return "-";
    },
    getOtType(val) {
      if (val == 1) {
        return "OT-1.5";
      } else if (val == 2) {
        return "OT-2";
      }
      return "-";
    },
    getDay(val) {
      let days = [];
      if (Array.isArray(val)) {
        days = val;
      } else if (typeof val === "string") {
        try {
          let parsed = JSON.parse(val);
          if (Array.isArray(parsed)) {
            days = parsed;
          } else {
            days = val.split(",");
          }
        } catch (e) {
          days = val.split(",");
        }
      } else if (val) {
        days = [val];
      }

      if (!days || !days.length) return "-";

      return days
        .map((d) => {
          switch (String(d)) {
            case "1":
              return "Monday";
            case "2":
              return "Tuesday";
            case "3":
              return "Wednesday";
            case "4":
              return "Thursday";
            case "5":
              return "Friday";
            case "6":
              return "Saturday";
            case "7":
              return "Sunday";
            default:
              return "-";
          }
        })
        .join(", ");
    },
    formatTime(time) {
      if (!time) return "-";

      return moment(time, "HH:mm:ss").format("hh:mm A");
    },

    deleteJob(id) {
      this.deleteCategoryDialog = true;
      this.deleteId = id;
    },
    saveOt() {
      this.showErrors = true;
      if (!this.$refs.otForm.validate()) {
        return;
      }

      // Check Duplicate Entry
      const duplicate = this.templateDataArr.find(
        (item) =>
          item.shift_type === this.otPayload.shift_type &&
          item.ot_type === this.otPayload.ot_type &&
          item.day === this.otPayload.day &&
          item.id !== this.otPayload.id
      );

      if (duplicate) {
        this.logError({
          response: {
            data: { errors: { day: ["This duplicate entry already exists."] } },
          },
        });
        return;
      }
      const payload = {
        ...this.otPayload,
        day: this.otPayload.day ? [this.otPayload.day] : [],

        start_time: this.otPayload.start_time
          ? moment(this.otPayload.start_time, "hh:mm A").format("HH:mm:ss")
          : null,

        end_time: this.otPayload.end_time
          ? moment(this.otPayload.end_time, "hh:mm A").format("HH:mm:ss")
          : null,
      };

      this.pageLoading = true;
      this.$store
        .dispatch(PATCH, {
          url: "setting/ot-management",
          data: payload,
        })
        .then(() => {
          this.OTDialog = false;
          this.getOtTemplates();
        })
        .catch((error) => {
          this.pageLoading = false;
          this.logError(error);
        });
    },
    getOtTemplates() {
      const _this = this;
      _this.$store
        .dispatch(GET, { url: "setting/ot-management" })
        .then(({ data }) => {
          _this.templateDataArr = data;
          // _this.rowsOffset = data.offset;
          // _this.totalRows = data.total_rows;
          // _this.totalPages = Math.ceil(data.total_rows / data.per_page);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    getBusinessHours(entityId) {
      const _this = this;
      _this.$store
        .dispatch(QUERY, {
          url: "setting/company",
          data: { entity: entityId },
        })
        .then(({ data }) => {
          this.companyBusinessHours = data.business_hours || [];
          this.companyNightBusinessHours = data.night_business_hours || [];
          console.log("business hours", data);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          // _this.pageLoading = false;
        });
    },
  },
  computed: {
    pageCount() {
      return Math.ceil(this.templateDataArr.length / this.itemsPerPage);
    },
    paginatedOtList() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.templateDataArr.slice(start, end);
    },
    showingFrom() {
      return this.lodash.isEmpty(this.templateDataArr) === false
        ? this.rowsOffset + 1
        : 0;
    },
    showingTo() {
      return (
        this.rowsOffset +
        (this.templateDataArr ? this.templateDataArr.length : 0)
      );
    },
  },
  mounted() {
    this.getOtTemplates();
    this.getBusinessHours(1);
  },
  components: {
    DeleteDialog,
    ListingTable,
    TableActivity,
    Dialog,
    TimePicker,
  },
};
</script>
<style scoped>
.ot-management-listing {
  position: relative;
  height: calc(100vh - 110px) !important;
  min-height: 0 !important;
  padding-bottom: 20px;
}

.ot-management-listing.tag-list-height
  ::v-deep
  .custom-listing-table
  .simple-table.custom-table-height
  .v-data-table__wrapper {
  max-height: calc(100vh - 170px) !important;
  height: 100% !important;
}

.ot-management-listing ::v-deep .v-data-table {
  min-height: 430px !important;
}

.ot-management-listing-footer {
  position: absolute;
  right: 12px;
  bottom: 0;
  left: 12px;
  z-index: 2;
  min-height: 56px;
  margin: 0 !important;
  background: #fff;
}
</style>
